"use client"

import { useState, useEffect, useCallback } from "react"
import {
  Search,
  Loader,
  AlertCircle,
  BookOpen,
  Clock,
  ChevronRight,
  CheckCircle,
  Filter,
  ChevronDown,
  Video,
  FileText,
  FileQuestion,
  TrendingUp,
  Award,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { generatePresignedUrl } from "../postFileAws"
import { getStudentInfo } from "./api"

const ELearning = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)

  const fetchCourses = useCallback(async () => {
    try {
      setIsLoading(true)
      const studentId = localStorage.getItem("studentId")
      if (!studentId) {
        throw new Error("No studentId found")
      }

      const response = await getStudentInfo()
      console.log("Fetched student data:", response)

      // Parse response if it's a string
      let studentData
      if (typeof response === "string") {
        studentData = JSON.parse(response)
      } else {
        studentData = response
      }

      console.log("Parsed student data:", studentData)

      // Check if response has the expected structure
      if (
        studentData &&
        studentData.body &&
        studentData.body.data &&
        Array.isArray(studentData.body.data.sharedCourses)
      ) {
        const sharedCourses = studentData.body.data.sharedCourses

        // Filter courses where shareWithStudents is true
        const availableCourses = sharedCourses.filter((course) => course.shareWithStudents === true)

        console.log("Available courses:", availableCourses)

        const formattedCourses = await Promise.all(
          availableCourses.map(async (course) => {
            let thumbnailUrl = null
            if (course.thumbnailS3Uri) {
              try {
                thumbnailUrl = await generatePresignedUrl(course.thumbnailS3Uri)
              } catch (error) {
                console.error("Error generating presigned URL:", error)
              }
            }

            // Calculate completion stats
            const completionStats = getCompletionStats(course.topics || [])

            return {
              id: course.courseId,
              title: course.title,
              description: course.description,
              thumbnail: thumbnailUrl,
              rating: course.rating || 4.5,
              enrolledStudents: course.enrolledStudents || 0,
              topics: course.topics || [],
              teacherId: course.teacherId,
              completionPercentage: completionStats.completionPercentage,
              totalSubtopics: completionStats.totalSubtopics,
              completedSubtopics: completionStats.completedSubtopics,
            }
          }),
        )
        setCourses(formattedCourses)
      } else {
        console.log("No shared courses found or invalid data structure")
        setCourses([])
      }
      setError(null)
    } catch (err) {
      console.error("Error fetching student courses:", err)
      setError("Failed to load courses. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCourses()
  }, [fetchCourses])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const getCompletionStats = (topics) => {
    let totalSubtopics = 0
    let completedSubtopics = 0

    topics.forEach((topic) => {
      topic.subtopics?.forEach((subtopic) => {
        totalSubtopics++
        if (subtopic.completed) {
          completedSubtopics++
        }
      })
    })

    const completionPercentage = totalSubtopics > 0 ? Math.round((completedSubtopics / totalSubtopics) * 100) : 0
    return { totalSubtopics, completedSubtopics, completionPercentage }
  }

  const getContentStats = (topics) => {
    let videoCount = 0
    let pdfCount = 0
    let quizCount = 0

    topics.forEach((topic) => {
      topic.subtopics?.forEach((subtopic) => {
        if (subtopic.contentType === "video") videoCount++
        if (subtopic.contentType === "pdf") pdfCount++
        if (subtopic.contentType === "quiz") quizCount++
      })
    })

    return { videoCount, pdfCount, quizCount }
  }

  const getCourseStatus = (course) => {
    if (course.completionPercentage === 100) return "completed"
    if (course.completionPercentage > 0) return "in-progress"
    return "not-started"
  }

  const getStatusCounts = () => {
    const total = courses.length
    let completed = 0
    let inProgress = 0
    let notStarted = 0
    let totalCompletionPercentage = 0

    courses.forEach((course) => {
      const status = getCourseStatus(course)
      totalCompletionPercentage += course.completionPercentage

      switch (status) {
        case "completed":
          completed++
          break
        case "in-progress":
          inProgress++
          break
        case "not-started":
          notStarted++
          break
      }
    })

    const averageCompletion = total > 0 ? Math.round(totalCompletionPercentage / total) : 0

    return { total, completed, inProgress, notStarted, averageCompletion }
  }

  const getFilteredCourses = () => {
    let filtered = courses

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((course) => getCourseStatus(course) === filterStatus)
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((course) => {
        const title = (course.title || "").toLowerCase()
        const description = (course.description || "").toLowerCase()
        const search = searchTerm.toLowerCase()
        return title.includes(search) || description.includes(search)
      })
    }

    return filtered
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case "in-progress":
        return <Clock className="w-5 h-5 text-amber-600" />
      default:
        return <BookOpen className="w-5 h-5 text-blue-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "border-t-green-500"
      case "in-progress":
        return "border-t-amber-500"
      default:
        return "border-t-blue-500"
    }
  }

  const getActionButton = (course, status) => {
    
  }

  const handleCourseClick = (course) => {
    navigate(`/eLearning/view/${course.id}`)
  }

  const statusCounts = getStatusCounts()
  const filteredCourses = getFilteredCourses()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-indigo-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading courses</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your courses...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null)
              fetchCourses()
            }}
            className="px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-xl max-w-md animate-fade-in flex items-center ${
            flashMessage.type === "success" ? "bg-green-500" : "bg-red-500"
          } text-white`}
        >
          {flashMessage.type === "success" ? (
            <CheckCircle className="mr-3 flex-shrink-0" size={20} />
          ) : (
            <AlertCircle className="mr-3 flex-shrink-0" size={20} />
          )}
          <p>{flashMessage.message}</p>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Stats, Search, and Filter */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* Stats - Compact for mobile */}
                <div className="flex items-center gap-1 mr-1">
                  <button
                    onClick={() => setFilterStatus("all")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "all" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.total}</p>
                    <p className="text-white/80 text-[8px]">Total</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus("completed")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "completed" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.completed}</p>
                    <p className="text-white/80 text-[8px]">Completed</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus("in-progress")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "in-progress" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.inProgress}</p>
                    <p className="text-white/80 text-[8px]">Progress</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus("not-started")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "not-started" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.notStarted}</p>
                    <p className="text-white/80 text-[8px]">New</p>
                  </button>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{statusCounts.averageCompletion}%</p>
                    <p className="text-white/80 text-[8px]">Avg</p>
                  </div>
                </div>

                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>

                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All" },
                        { key: "completed", label: "Completed" },
                        { key: "in-progress", label: "In Progress" },
                        { key: "not-started", label: "Not Started" },
                      ].map((status) => (
                        <button
                          key={status.key}
                          onClick={() => {
                            setFilterStatus(status.key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left transition-colors hover:bg-gray-50 ${
                            filterStatus === status.key ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* Stats - Left side */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  {
                    icon: <BookOpen className="w-4 h-4 opacity-80" />,
                    label: "Total",
                    value: statusCounts.total,
                    key: "all",
                  },
                  {
                    icon: <CheckCircle className="w-4 h-4 opacity-100" />,
                    label: "Completed",
                    value: statusCounts.completed,
                    key: "completed",
                  },
                  {
                    icon: <Clock className="w-4 h-4 opacity-80" />,
                    label: "In Progress",
                    value: statusCounts.inProgress,
                    key: "in-progress",
                  },
                  {
                    icon: <BookOpen className="w-4 h-4 opacity-80" />,
                    label: "Not Started",
                    value: statusCounts.notStarted,
                    key: "not-started",
                  },
                ].map((item, index) => (
                  <button
                    key={index}
                    onClick={() => setFilterStatus(item.key)}
                    className={`inline-flex items-center gap-2 border border-white/30 backdrop-blur-sm rounded-md px-3 py-1 text-white text-xs font-medium whitespace-nowrap transition-colors ${
                      filterStatus === item.key ? "bg-white/20" : "bg-white/10 hover:bg-white/15"
                    }`}
                  >
                    {item.icon}
                    <span>
                      {item.label}: <span className="font-bold">{item.value}</span>
                    </span>
                  </button>
                ))}
                {/* Average Completion - Non-clickable */}
                <div className="inline-flex items-center gap-2 border border-white/30 backdrop-blur-sm rounded-md px-3 py-1 text-white text-xs font-medium whitespace-nowrap bg-white/10">
                  <TrendingUp className="w-4 h-4 opacity-80" />
                  <span>
                    Avg Progress: <span className="font-bold">{statusCounts.averageCompletion}%</span>
                  </span>
                </div>
              </div>

              {/* Search and Filter - Right side */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search courses"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>

                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>
                      {filterStatus === "all"
                        ? "All"
                        : filterStatus === "in-progress"
                          ? "In Progress"
                          : filterStatus === "not-started"
                            ? "Not Started"
                            : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All" },
                        { key: "completed", label: "Completed" },
                        { key: "in-progress", label: "In Progress" },
                        { key: "not-started", label: "Not Started" },
                      ].map((status) => (
                        <button
                          key={status.key}
                          onClick={() => {
                            setFilterStatus(status.key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                            filterStatus === status.key ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-3 md:p-6">
            {/* Courses Grid */}
            {courses.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No courses available</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Your teacher hasn't shared any courses yet. Check back later for new courses.
                </p>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-4">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching courses found</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Try adjusting your search terms or filters to find what you're looking for.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setFilterStatus("all")
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredCourses.map((course) => {
                  const status = getCourseStatus(course)
                  const contentStats = getContentStats(course.topics || [])
                  return (
                    <div
                      key={course.id}
                      className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col relative group max-w-xs mx-auto w-full cursor-pointer border-t-4 ${getStatusColor(
                        status,
                      )}`}
                      onClick={() => handleCourseClick(course)}
                    >
                      {/* Course Thumbnail */}
                      <div className="relative h-32 overflow-hidden">
                        {course.thumbnail ? (
                          <img
                            src={course.thumbnail || "/placeholder.svg"}
                            alt={course.title}
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center">
                            <BookOpen className="w-12 h-12 text-white opacity-80" />
                          </div>
                        )}
                        {/* Status Icon */}
                        <div className="absolute top-2 right-2  p-1.5">
                          {getStatusIcon(status)}
                        </div>
                      </div>

                      {/* Course Content */}
                      <div className="p-4 flex-grow flex flex-col">
                        <h3 className="text-white font-semibold line-clamp-2 text-sm mb-2 bg-gradient-to-r from-indigo-600 to-blue-600 px-3 py-2 rounded-lg">
                          {course.title}
                        </h3>

                        {course.description && (
                          <p className="text-gray-600 text-xs mb-3 line-clamp-2">{course.description}</p>
                        )}

                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{course.completionPercentage}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${course.completionPercentage}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Content Stats */}
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <div className="flex items-center space-x-3">
                            {contentStats.videoCount > 0 && (
                              <div className="flex items-center">
                                <Video className="w-3 h-3 mr-1" />
                                <span>{contentStats.videoCount}</span>
                              </div>
                            )}
                            {contentStats.pdfCount > 0 && (
                              <div className="flex items-center">
                                <FileText className="w-3 h-3 mr-1" />
                                <span>{contentStats.pdfCount}</span>
                              </div>
                            )}
                            {contentStats.quizCount > 0 && (
                              <div className="flex items-center">
                                <FileQuestion className="w-3 h-3 mr-1" />
                                <span>{contentStats.quizCount}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-auto space-y-1.5 pt-2 border-t border-gray-100">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span className="flex items-center">
                              <Clock size={12} className="mr-1.5" />
                              Lessons:
                            </span>
                            <span>
                              {course.completedSubtopics}/{course.totalSubtopics}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3">{getActionButton(course, status)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ELearning
